import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import DebounceInput from "react-debounce-input";
import { toast } from "react-toastify";
import { Button, ButtonGroup, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import { CustomerTypeahead, ItemTypeahead, Pager, ServiceThreadSpinner, SortHeader } from ".";
import { DesktopContainer } from "../layouts";
import { api, helpers, UserContext } from "../utils";

const CLASSES_LOAD_FAILED_MESSAGE = "The machine part pricing for this customer could not be loaded. If this persists, contact a system admin.";

export default function CustomerMachinePartPricingMaintenance(props) {
   const userCtx = useContext(UserContext);
   // Cost, markup and margin columns are hidden (the server also blanks them) for anyone outside the cost roles.
   const canSeeCost = helpers.canSeeCostAndMargin(userCtx.currentUser);
   const modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";
   const [classOptions, setClassOptions] = useState([]);
   const [confirmDelete, setConfirmDelete] = useState(false);
   const [customer, setCustomer] = useState(null);
   const [customerDataLoaded, setCustomerDataLoaded] = useState(false);
   const [data, setData] = useState([]);
   const [deleteIndex, setDeleteIndex] = useState(null);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [filter, setFilter] = useState(null);
   const [initialized, setInitialized] = useState(false);
   const [itemFilter, setItemFilter] = useState(null);
   const [loading, setLoading] = useState(false);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(25);
   const [saving, setSaving] = useState(false);
   const [sortDirection, setSortDirection] = useState("asc");
   const [sortField, setSortField] = useState("listPrice");
   const [totalPages, setTotalPages] = useState();

   useEffect(() => {
      if (!initialized) {
         setCustomer(userCtx.customerFilter);
         setInitialized(true);
      }
      if (initialized && customer) {
         runQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userCtx, initialized, customer]);

   useEffect(() => {
      if (filter && !customerDataLoaded) {
         getCustomerParts();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [customerDataLoaded, filter, sortDirection, sortField, itemFilter]);

   function allowEdit(index) {
      setEditIndex(index);
      if (!editCopy || !editCopy.itemNumber) {
         const itemCopy = Object.assign({}, classOptions[index]);
         setEditCopy(itemCopy);
      }
   }

   function cancelEdit() {
      setEditIndex(null);
      setEditCopy(null);
   }

   function changeFilter(classId) {
      setFilter(classId);
      setCustomerDataLoaded(false);
      setData([]);
      setTotalPages(null);
   }

   function deleteCustomerItemPricing(idx, overrideClose) {
      let newList = Object.assign([], data);
      newList = _.map(newList, (x, index) => {
         x.delete = _.isBoolean(overrideClose)
            ? overrideClose
            : idx === index
               ? !x.delete
               : false;
         return x;
      });
      setData(newList);
      if (
         _.some(newList, (x) => {
            return x.delete;
         })
      ) {
         setDeleteIndex(idx);
         setConfirmDelete(true);
      }
   }

   function getCustomerParts() {
      if (customer === null || filter === null) {
         setCustomerDataLoaded(false);
         setData([]);
         setTotalPages(null);
         return;
      }
      const payload = {
         CustomerId: _.trim(userCtx.customerFilter.value),
         ItemNumberSearch: itemFilter ? itemFilter.itemNumber : "",
         PageNumber: page,
         PageSize: pageSize,
         ItemClassCode: filter,
      };

      return api
         .post("Customer/GetPaginatedCustomerMachinePartRetailPricing", payload)
         .then((response) => {
            if (response.data.success) {
               const sortedData = _.chain(response.data?.message?.list)
                  .orderBy([sortField], [sortDirection])
                  .drop(pageSize * (page - 1))
                  .take(pageSize)
                  .value();
               setData(sortedData);
               setTotalPages(response?.data?.message.totalPages);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setCustomerDataLoaded(true));
   }

   function handleChange(field, value, limitTo100) {
      if (typeof value === "string" && _.isEmpty(value)) value = 0;
      if (
         parseInt(value, 10) >= 0 &&
         (limitTo100 ? parseInt(value, 10) <= 100 : true)
      ) {
         let changes = Object.assign({}, editCopy);
         changes[field] = parseInt(value, 10);
         changes.isInvalid = false;
         setEditCopy(changes);
      } else {
         let changes = Object.assign({}, editCopy);
         changes.isInvalid = true;
         setEditCopy(changes);
         toast.warning(
            "Value must be a valid numeric amount. Must be a positive value and percentages cannot exceed 100"
         );
      }
   }

   function handleCustomerItemChange(field, value, idx) {
      let newList = Object.assign([], data);
      let changes = Object.assign({}, newList[idx]);
      changes[field] = value;
      newList[idx] = changes;
      setData(newList);
   }

   function handleCustomerItemDelete(index) {
      let item = Object.assign({}, data[index]);
      if (item.pricingId > 0) {
         setSaving(true);
         api
            .fetch(`Item/DeleteCustomerMachinePartPricing/${item.overridePriceID}`)
            .then((res) => {
               if (res) {
                  setConfirmDelete(false);
                  setDeleteIndex(null);
                  deleteCustomerItemPricing(deleteIndex, false);
                  getCustomerParts();
                  toast.success("Data deleted successfully.");
               } else {
                  toast.warning("Unable to delete this row" + res?.data?.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setSaving(false));
      } else {
         toast.warning("Must having pricing data before you can delete it.");
         setConfirmDelete(false);
         setDeleteIndex(null);
      }
   }

   function handleDelete(item) {
      if (item.pricingId > 0) {
         setSaving(true);
         api
            .fetch(`Item/DeleteMachinePartPricing/${item.pricingId}`)
            .then((res) => {
               if (res) {
                  setConfirmDelete(false);
                  setDeleteIndex(null);
                  runQuery();
                  toast.success("Data deleted successfully.");
               } else {
                  toast.warning("Unable to delete this row" + res?.data?.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setSaving(false));
      } else {
         toast.warning("Must having pricing data before you can delete it.");
         setConfirmDelete(false);
         setDeleteIndex(null);
      }
   }

   function handleEditClick(index) {
      if (editIndex === index) {
         saveClass();
      } else {
         allowEdit(index);
      }
   }

   function onPageChange(page) {
      setPage(page);
      setCustomerDataLoaded(false);
   }

   function onPageSizeChange(size) {
      setPageSize(size);
      setTotalPages(null);
      onPageChange(1);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
      setCustomerDataLoaded(false);
   }

   function runQuery() {
      if (!loading) {
         if (customer === null) {
            setLoading(false);
            setClassOptions([]);
            setData([]);
            setTotalPages(null);
            return;
         }
         setLoading(true);
         const payload = {
            CustomerId: _.trim(userCtx.customerFilter.value),
         };
         return api
            .post("SewingTech/GetMachinePartClassesByCustomer", payload)
            .then((response) => {
               // On a failure clear the classes, so the previous customer's pricing is not left on screen as this one's.
               if (api.toastFailure(response, CLASSES_LOAD_FAILED_MESSAGE)) {
                  setClassOptions([]);
                  return;
               }
               setClassOptions(_.sortBy(response.data.list, (x) => x.classDescription));
            })
            .catch(api.catchHandler)
            .finally(() => setLoading(false));
      }
   }

   function saveClass() {
      let payload = {
         CustomerId: customer.value,
         PricingId: editCopy?.pricingId,
         PercentOffList: parseFloat(editCopy?.percentOffList),
         MinimumMarkup: parseFloat(editCopy?.minimumMarkup),
         MaxAmountOffRetail: parseInt(editCopy.maxAmountOffRetail),
         MachinePartClassId: editCopy.machinePartClassId,
      };
      // need to figure out what this checkbox was doing
      // Dim savePriceInfo As Boolean = CType(gvCustomerPricing.Rows(e.RowIndex).FindControl("chkHasPricingInfo"), CheckBox).Checked
      setSaving(true);
      api
         .post("Item/SaveMachinePartPricing", payload)
         .then((response) => {
            if (response) {
               setEditIndex(null);
               setEditCopy(null);
               runQuery();
               toast.success("Row saved successfully.");
            } else {
               toast.warning("Unable to save this row" + response?.data?.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setSaving(false));
   }

   function showConfirmModal(index) {
      setConfirmDelete(true);
      setDeleteIndex(index);
   }

   function saveCustomerItemsChanges(index) {
      setSaving(true);
      let changes = Object.assign({}, data[index]);
      const payload = {
         CustomerId: _.trim(userCtx.customerFilter.value),
         OverridePrice: parseFloat(changes.overridePrice),
         OverridePriceID: parseFloat(changes.overridePriceID),
         ItemNumber: _.trim(changes.itemNumber),
      };
      if (parseFloat(changes.overridePrice) > 0) {
         payload.OverridePrice = parseFloat(changes.overridePrice);
      }
      api
         .post(`Item/SaveCustomerMachinePartPricing`, payload)
         .then((response) => {
            if (response) {
               toggleEditRow(index, true);
               getCustomerParts();
               toast.success("Row saved successfully.");
            } else {
               toast.warning("Unable to save this row" + response?.data?.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setSaving(false));
   }

   function toggleEditRow(idx, resetOverride) {
      let newList = Object.assign([], data);
      newList = _.map(newList, (x, index) => {
         x.editing = resetOverride || idx === index ? !x.editing : false;
         return x;
      });
      setData(newList);
   }


   return (
      document.title = `Customer Machine Part Pricing`,
      <DesktopContainer screenName={"Machine Part Pricing"}>
         <div className="filter-header mb-3">
            <Row className="justify-content-center mb-2">
               <Col xs={3} lg={1} className="text-end">
                  <h4 className="pt-1 text-white" children="Customer: " />
               </Col>
               <Col style={{ marginLeft: "25px" }} xs={9} lg={7}>
                  <CustomerTypeahead
                     value={customer}
                     onChange={(e) => {
                        if (_.isNil(e)) {
                           setData([]);
                           setFilter(null);
                           setTotalPages(null);
                        }
                        setCustomer(e);
                        setFilter(null);
                     }}
                  />
               </Col>
            </Row>
         </div>
         <ServiceThreadSpinner on={loading}>
            {classOptions?.length && customer ? (
               <>
                  {" "}
                  <Table className="data-table text-center">
                     <thead className="table-header">
                        <tr>
                           <th className="machinePartTable">
                              Has
                              <br />
                              Access
                           </th>
                           <th>Class</th>
                           <th width="20%">% Off List</th>
                           <th width="20%">Minimum Markup %</th>
                           <th width="20%">Max $ Off Retail</th>
                           <th />
                           <th>View Prices</th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(classOptions, (c, index) => {
                           const editingThisItem = editIndex === index;
                           if (filter && filter !== c.itemClass) return null;
                           return (
                              <tr className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`} key={index} >
                                 <td className="data-cell">
                                    <Input
                                       type="checkbox"
                                       className="me-3"
                                       checked={c.hasPricingInfo}
                                       onChange={() => showConfirmModal(index)}
                                       disabled={
                                          (editIndex !== null && editIndex !== index) ||
                                          !c.hasPricingInfo ||
                                          saving
                                       }
                                    />
                                 </td>
                                 <td className="data-cell">{c.itemClassDescription}</td>
                                 <td className="data-cell">
                                    {editingThisItem ? (
                                       <DebounceInput
                                          type="number"
                                          value={editCopy.percentOffList ?? 0}
                                          onChange={(e) => {
                                             handleChange("percentOffList", e.target.value, true ); // true limits it to 100
                                          }}
                                          debounceTimeout={300}
                                          className="float-start form-control"
                                       />
                                    ) : (
                                       helpers.formatWithDecimalPlaces(c.percentOffList)
                                    )}
                                 </td>
                                 <td className="data-cell">
                                    {editingThisItem ? (
                                       <DebounceInput
                                          type="number"
                                          value={editCopy.minimumMarkup ?? 0}
                                          onChange={(e) => {
                                             handleChange("minimumMarkup", e.target.value, true); // true limits it to 100
                                          }}
                                          debounceTimeout={300}
                                          className="float-start form-control"
                                       />
                                    ) : (
                                       helpers.formatWithDecimalPlaces(c.minimumMarkup, 2)
                                    )}
                                 </td>
                                 <td className="data-cell">
                                    {editingThisItem ? (
                                       <DebounceInput
                                          type="number"
                                          value={editCopy.maxAmountOffRetail ?? 0}
                                          onChange={(e) => { handleChange("maxAmountOffRetail", e.target.value); }}
                                          debounceTimeout={300}
                                          className="float-start form-control"
                                       />
                                    ) : (
                                       helpers.formatCurrency(c.maxAmountOffRetail)
                                    )}
                                 </td>
                                 <td className="data-cell">
                                    <ButtonGroup>
                                       <Button
                                          className="icon-button me-2 float-end"
                                          title={editingThisItem ? "Save changes" : "Edit line"}
                                          color={editingThisItem ? "success" : "info"}
                                          onClick={() => handleEditClick(index)}
                                          disabled={
                                             saving ||
                                             editCopy?.isInvalid ||
                                             (editIndex !== null && editIndex !== index)
                                          }
                                       >
                                          <FontAwesomeIcon
                                             className="whiteIcon"
                                             icon={editingThisItem ? "check" : "edit"}
                                          />
                                       </Button>
                                       <Button
                                          className="icon-button float-end me-1"
                                          color={editingThisItem ? "secondary" : "danger"}
                                          title={editingThisItem ? "Discard Changes": "Remove From Cart"}
                                          onClick={
                                             editingThisItem
                                                ? () => cancelEdit()
                                                : () => showConfirmModal(index)
                                          }
                                          disabled={
                                             saving ||
                                             ((editIndex !== null || props.isEditing) &&
                                                editIndex !== index)
                                          }
                                       >
                                          <FontAwesomeIcon
                                             className="whiteIcon"
                                             icon={editingThisItem ? "times" : "trash"}
                                          />
                                       </Button>
                                    </ButtonGroup>
                                 </td>
                                 <td className="data-cell">
                                    <Button
                                       onClick={() => {
                                          filter
                                             ? setFilter(null)
                                             : changeFilter(c.itemClass);
                                       }}
                                       className="no-decoration"
                                    >
                                       {filter ? "Hide" : "View"}
                                    </Button>
                                 </td>
                              </tr>
                           );
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan="7" />
                        </tr>
                     </tfoot>
                  </Table>
               </>
            ) : (
               <div className="h5 text-center mt-3">Please select a customer</div>
            )}
            <Modal
               id="confirm-delete"
               isOpen={confirmDelete}
               contentClassName={modalClassName}
            >
               <ModalHeader children="Confirm Delete" />
               <ModalBody>Are you sure you want to delete this?</ModalBody>
               <ModalFooter>
                  <ButtonGroup>
                     <Button
                        children="Yes"
                        className="button-success"
                        onClick={() => {
                           let customerItemMarked = _.some(data, (x) => {
                              return x.delete;
                           });
                           if (customerItemMarked) {
                              handleCustomerItemDelete(deleteIndex);
                           } else {
                              handleDelete(classOptions[deleteIndex]);
                           }
                        }}
                     />
                     <Button
                        children="No"
                        className="button-danger"
                        onClick={() => {
                           setConfirmDelete(false);
                           setDeleteIndex(null);
                           deleteCustomerItemPricing(deleteIndex, false);
                        }}
                     />
                  </ButtonGroup>
               </ModalFooter>
            </Modal>
         </ServiceThreadSpinner>
         {filter ? (
            <ServiceThreadSpinner on={!customerDataLoaded}>
               <ItemTypeahead
                  name="itemFilter"
                  value={itemFilter}
                  onChange={(e) => {
                     setPage(1);
                     setCustomerDataLoaded(false);
                     setItemFilter(e);
                  }}
               />
               <Row className="align-items-center mb-2">
                  <Col className="transparent-background">
                     <Pager
                        callBack={onPageChange}
                        sizeCallback={onPageSizeChange}
                        pageNumber={page}
                        pageSize={pageSize}
                        totalPages={totalPages}
                        disabled={props.downloading}
                     />
                  </Col>
               </Row>
               {data.length ? (
                  <Table className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th>
                              <SortHeader
                                 displayName="Item Number"
                                 field="customerItemNumber"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "customerItemNumber"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Description"
                                 field="itemDescription"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "itemDescription"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="In Stock"
                                 field="stockStatus"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "stockStatus"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="List Price"
                                 field="listPrice"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "listPrice"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Master Rep Price"
                                 field="masterRepPrice"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "masterRepPrice"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Customer Price"
                                 field="customerCost"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "customerCost"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName={
                                    <span>
                                       {" "}
                                       Price
                                       <br />
                                       Override
                                    </span>
                                 }
                                 field="overridePrice"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "overridePrice"}
                                 callback={onSortChange}
                              />
                           </th>
                           {/* The edit/delete buttons column - its blank header sits BEFORE Cost to match the row cells. */}
                           <th />
                           {canSeeCost && (
                              <>
                                 <th>
                                    <SortHeader
                                       displayName="Cost"
                                       field="currentCost"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "currentCost"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName={
                                          <span>
                                             {" "}
                                             Mark Up
                                             <br />%
                                          </span>
                                       }
                                       field="markUpPercent"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "markUpPercent"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName="Profit Margin"
                                       field="profitMargin"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "profitMargin"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName={
                                          <span>
                                             Gross
                                             <br /> Margin %
                                          </span>
                                       }
                                       field="grossMarginPercent"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "grossMarginPercent"}
                                       callback={onSortChange}
                                    />
                                 </th>
                              </>
                           )}
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (m, idx) => (
                           <tr
                              className={`tr-${idx % 2 === 0 ? "primary" : "secondary"}`}
                              key={idx}
                           >
                              <td className="data-cell">{m.customerItemNumber}</td>
                              <td className="data-cell">{m.itemDescription}</td>
                              <td className="data-cell">{m.stockStatus}</td>
                              <td className="data-cell">{helpers.formatCurrency(m.listPrice)}</td>
                              <td className="data-cell">{helpers.formatCurrency(m.masterRepPrice)}</td>
                              <td className="data-cell">
                                 <div
                                    className={
                                       m.masterRepPrice < m.customerCost && m.listPrice > 0
                                          ? "text-brightRed"
                                          : ""
                                    }
                                 >
                                    {helpers.formatCurrency(m.customerCost)}
                                 </div>
                              </td>
                              <td className="data-cell">
                                 {m.editing ? (
                                    <DebounceInput
                                       type="number"
                                       value={m.overridePrice}
                                       onChange={(e) => { handleCustomerItemChange("overridePrice", e.target.value, idx); }}
                                       debounceTimeout={300}
                                       className="float-start form-control"
                                    />
                                 ) : m.overridePrice > 0 ? (
                                    helpers.formatCurrency(m.overridePrice)
                                 ) : (
                                    `n/a`
                                 )}
                              </td>
                              <td className="data-cell">
                                 <ButtonGroup className="btn-group-sm btn-sm  float-end">
                                    <Button
                                       color={m.editing ? "success" : "info"}
                                       className="icon-button"
                                       title={m.editing ? "Save changes" : "Edit Price Override"}
                                       onClick={() => {
                                          m.editing
                                             ? saveCustomerItemsChanges(idx)
                                             : toggleEditRow(idx);
                                       }}
                                       disabled={saving}
                                    >
                                       <FontAwesomeIcon
                                          className="whiteIcon"
                                          icon={m.editing ? "check" : "edit"}
                                       />
                                    </Button>
                                    <Button
                                       color={m.editing ? "secondary" : "danger"}
                                       className="icon-button"
                                       title={m.editing ? "Cancel" : "Delete"}
                                       onClick={() => {
                                          m.editing
                                             ? toggleEditRow(idx)
                                             : deleteCustomerItemPricing(idx);
                                       }}
                                       disabled={saving}
                                    >
                                       <FontAwesomeIcon
                                          className="whiteIcon"
                                          icon={m.editing ? "times" : "trash"}
                                       />
                                    </Button>
                                 </ButtonGroup>
                              </td>
                              {canSeeCost && (
                                 <>
                                    <td className="data-cell">
                                       {helpers.formatCurrency(m.currentCost)}
                                    </td>
                                    <td className="data-cell">
                                       {helpers.formatPercent(m.markUpPercent)}
                                    </td>
                                    <td className="data-cell">
                                       {helpers.formatCurrency(m.profitMargin)}
                                    </td>
                                    <td className="data-cell">
                                       {helpers.formatPercent(m.grossMarginPercent)}
                                    </td>
                                 </>
                              )}
                           </tr>
                        ))}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={canSeeCost ? 12 : 8} />
                        </tr>
                     </tfoot>
                  </Table>
               ) : (
                  <span style={{ display: "block", textAlign: "center", fontSize: "1.2em" }} >
                     This customer does not have any machine parts under this class.
                  </span>
               )}
            </ServiceThreadSpinner>
         ) : null}
      </DesktopContainer>
   );
}
